import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import GalleryEditModal from './GalleryEditModal';
import GalleryCreateModal from './GalleryCreateModal';
import { Gallery } from '../../types/Gallery';
import sitePaths from '../../utils/sitePaths';
import { getCategoryOptions } from '../../utils/categories';
import { dataSync } from '../../utils/dataSync';
import { saveGalleriesToServer, loadLiveGalleries, syncGalleriesWithCloudinary } from '../../utils/GalleryAPI';
import { applyHeroImagePositioning, getCSSObjectPosition } from '../../utils/ImagePositioning';
import './GalleryManagement.css';
import './ui-enhancements.css';
import '../ui-master-styles.css';

interface GalleryManagementProps {
  galleries: Gallery[];
  onGalleriesChange: (galleries: Gallery[]) => void;
}

const GalleryManagement: React.FC<GalleryManagementProps> = ({ galleries, onGalleriesChange }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [galleryToEdit, setGalleryToEdit] = useState<Gallery | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<{value: string; label: string; color: string}[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isSyncingCloudinary, setIsSyncingCloudinary] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuBox, setMenuBox] = useState<{ top: number; left: number } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOrder, setDragOrder] = useState<Gallery[] | null>(null);
  const dragSession = useRef<{ pointerId: number; sourceId: string; origin: Gallery[] } | null>(null);
  const latestOrder = useRef<Gallery[] | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const categoryOptionsWithCounts = useMemo(() => {
    return categories
      .map(cat => ({
        value: cat.slug,
        label: cat.name,
        color: cat.color || '#8B4513',
        count: galleries.filter(gallery => sitePaths.galleryAssignedToCategory(gallery, cat)).length,
      }))
      .filter(opt => opt.value && (opt.count > 0 || opt.value === selectedCategory));
  }, [categories, galleries, selectedCategory]);

  const galleryPreviewHref = (gallery: Gallery): string =>
    sitePaths.galleryPath(gallery);

  const stopCardDrag = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const categoryLabel = (categoryName: string): string => {
    const normalizedInput = (categoryName || '').toLowerCase().trim();
    const category = categories.find(cat => {
      const normalizedName = (cat.name || '').toLowerCase().trim();
      const normalizedSlug = (cat.slug || '').toLowerCase().trim();
      return normalizedName === normalizedInput || normalizedSlug === normalizedInput;
    });
    return category?.name || categoryName;
  };

  const coverNotes = (gallery: Gallery): string[] => {
    const notes: string[] = [];
    if (gallery.heroImageZoom && gallery.heroImageZoom !== 1) {
      notes.push(`Cover zoom ${gallery.heroImageZoom}×`);
    }
    const cropBox = gallery.heroImageCropBox;
    if (
      cropBox &&
      cropBox.width > 0 &&
      cropBox.height > 0 &&
      !(cropBox.x === 25 && cropBox.y === 25 && cropBox.width === 50 && cropBox.height === 50)
    ) {
      notes.push('Crop in use');
    }
    return notes;
  };

  // Load dynamic categories on component mount and listen for updates
  useEffect(() => {
    const loadCategories = () => {
      const options = getCategoryOptions();
      setCategoryOptions(options);
    };

    const loadCategoriesData = async () => {
      try {
        const { loadLiveCategories } = await import('../../utils/CategoryAPI');
        const categoriesData = await loadLiveCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
        // Fallback to localStorage
        const localCategories = JSON.parse(localStorage.getItem('admin_categories') || '[]');
        setCategories(localCategories);
      }
    };

    loadCategories();
    loadCategoriesData();

    // If Quick Action requested opening create gallery, honor it once
    try {
      const action = localStorage.getItem('admin_action');
      if (action === 'open_create_gallery') {
        setShowCreateModal(true);
        localStorage.removeItem('admin_action');
      }
    } catch {}

    // Listen for category updates from admin panel
    const handleCategoryUpdate = () => {
      loadCategories();
      loadCategoriesData();
    };

    // Listen for storage events (when categories are updated in another tab/component)
    window.addEventListener('storage', handleCategoryUpdate);
    
    // Listen for custom category update events
    window.addEventListener('categories_updated', handleCategoryUpdate);

    return () => {
      window.removeEventListener('storage', handleCategoryUpdate);
      window.removeEventListener('categories_updated', handleCategoryUpdate);
    };
  }, []);



  const saveGalleries = async (updatedGalleries: Gallery[]) => {
    try {
      console.log('💾 saveGalleries called with', updatedGalleries.length, 'galleries');
      
      // Ensure all galleries have an order property
      const galleriesWithOrder = updatedGalleries.map((gallery, index) => ({
        ...gallery,
        order: gallery.order || (index + 1)
      }));
      
      // Log hero positioning settings being saved
      galleriesWithOrder.forEach(gallery => {
        // Clear crop box if it's empty/null/undefined
        if (gallery.heroImageCropBox && 
            (!gallery.heroImageCropBox.x && gallery.heroImageCropBox.x !== 0) &&
            (!gallery.heroImageCropBox.y && gallery.heroImageCropBox.y !== 0) &&
            (!gallery.heroImageCropBox.width && gallery.heroImageCropBox.width !== 0) &&
            (!gallery.heroImageCropBox.height && gallery.heroImageCropBox.height !== 0)) {
          gallery.heroImageCropBox = undefined;
        }
        
        if (gallery.heroImagePosition || gallery.heroImageCropBox) {
          console.log('💾 Saving gallery with positioning:', {
            id: gallery.id,
            name: gallery.name,
            heroImagePosition: gallery.heroImagePosition,
            heroImageZoom: gallery.heroImageZoom,
            heroImageCropBox: gallery.heroImageCropBox,
            updatedAt: gallery.updatedAt
          });
        }
      });
      
      
      const formattedGalleries = galleriesWithOrder.map(gallery => ({
        id: gallery.id,
        name: gallery.name,
        description: gallery.description || '',
        excerpt: gallery.excerpt || '',
        images: gallery.images.map(img => {
          const publicId = img.publicId || img.public_id || 'unknown';
          const url = img.url || img.secure_url || '';
          return {
            publicId: publicId, // Required field
            public_id: publicId,
            url: url,
            secure_url: img.secure_url || url,
            width: img.width || 0,
            height: img.height || 0,
            alt: img.alt || gallery.name,
            // Only include essential fields to avoid server errors
          };
        }),
        heroImage: gallery.heroImage || (gallery.images && gallery.images[0] && {
          publicId: gallery.images[0].publicId || gallery.images[0].public_id || 'unknown',
          public_id: gallery.images[0].publicId || gallery.images[0].public_id || 'unknown',
          url: gallery.images[0].url || gallery.images[0].secure_url || '',
          secure_url: gallery.images[0].secure_url || gallery.images[0].url || '',
          width: gallery.images[0].width || 0,
          height: gallery.images[0].height || 0,
          alt: gallery.images[0].alt || gallery.name,
        }) || {
          publicId: 'placeholder',
          url: '/placeholder.jpg',
          alt: gallery.name,
          width: 400,
          height: 300
        },
        heroImageIndex: gallery.heroImageIndex || 0,
        category: gallery.category,
        categories: Array.isArray(gallery.categories) ? gallery.categories : undefined,
        galleryType: gallery.galleryType || 'mosaic', // Preserve gallery type
        published: gallery.published !== undefined ? gallery.published : true, // Preserve published status
        createdAt: gallery.createdAt,
        updatedAt: gallery.updatedAt,
        order: gallery.order, // Preserve gallery ordering
        // CRITICAL: Include hero image positioning settings for server-side meta tags
        heroImagePosition: gallery.heroImagePosition,
        heroImageZoom: gallery.heroImageZoom,
        heroImageCustomX: gallery.heroImageCustomX,
        heroImageCustomY: gallery.heroImageCustomY,
        heroImageCropBox: gallery.heroImageCropBox
      }));
      
      // Always save to localStorage first (immediate backup)
      localStorage.setItem('admin_galleries', JSON.stringify(galleriesWithOrder));
      localStorage.setItem('react_galleries', JSON.stringify(galleriesWithOrder));
      
      // Save to server (truly live for all visitors) - use formatted to avoid server errors
      // This MUST succeed for cross-device sync to work
      console.log('💾 Attempting to save to server...');
      const success = await saveGalleriesToServer(formattedGalleries);
      
      // Mark in localStorage whether server save succeeded (for smart fallback on reload)
      if (success) {
        console.log('✅ Galleries saved to server - now live for all visitors!');
        localStorage.setItem('gallery_server_save_status', 'success');
        localStorage.setItem('gallery_server_save_timestamp', new Date().toISOString());
        // Clear any previous failure markers
        localStorage.removeItem('gallery_local_image_count');
      } else {
        console.error('❌ Server save FAILED - data only saved to localStorage');
        console.error('❌ This means other devices/computers will NOT see your changes!');
        console.error('❌ Check server logs for detailed error information');
        localStorage.setItem('gallery_server_save_status', 'failed');
        localStorage.setItem('gallery_server_save_timestamp', new Date().toISOString());
        // Store total image count so we can compare on reload
        const totalImages = galleriesWithOrder.reduce((sum, g) => sum + (g.images?.length || 0), 0);
        localStorage.setItem('gallery_local_image_count', totalImages.toString());
        
        // Only show alert in development - in production, log to console and let retries handle it
        const isProd = process.env.NODE_ENV === 'production';
        if (!isProd) {
          alert(`⚠️ Warning: Could not save to server!\n\nYour changes are saved locally, but other devices won't see them.\n\nPlease check:\n1. Server is running\n2. Network connection\n3. Server logs for errors\n\nTotal images: ${totalImages}`);
        } else {
          // In production, just log - the retry logic will handle it
          console.error(`❌ Production: Server save failed with ${totalImages} total images. Retries will be attempted.`);
        }
      }
      
      // Update parent component and notify listeners (homepage pills, etc.)
      onGalleriesChange(galleriesWithOrder);
      try {
        window.dispatchEvent(new CustomEvent('galleries_updated'));
      } catch (e) {
        console.warn('Failed to dispatch galleries_updated event', e);
      }
    } catch (error) {
      console.error('Error saving galleries:', error);
    }
  };

  const handleCreateGallery = (newGallery: Gallery) => {
    // Assign proper order to new gallery
    const galleryWithOrder = {
      ...newGallery,
      order: galleries.length + 1
    };
    const updatedGalleries = [...galleries, galleryWithOrder];
    saveGalleries(updatedGalleries);
    setShowCreateModal(false);
  };


  const deleteGallery = (galleryId: string) => {
    const galleryToDelete = galleries.find(g => g.id === galleryId);
    if (!galleryToDelete) return;

    const confirmMessage = `Delete Gallery: "${galleryToDelete.name}"?\n\n📊 Gallery Summary:\n• Images: ${galleryToDelete.images.length} total\n\n⚠️ This action cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) return;

    const updatedGalleries = galleries.filter(g => g.id !== galleryId);
    saveGalleries(updatedGalleries);
  };

  const handleEditGallery = (updatedGallery: Gallery) => {
    console.log('Saving gallery:', updatedGallery);
    const updatedGalleries = galleries.map(g => 
      g.id === updatedGallery.id ? updatedGallery : g
    );
    console.log('Updated galleries:', updatedGalleries);
    saveGalleries(updatedGalleries);
    setShowEditModal(false);
    setGalleryToEdit(null);
  };

  const handleSyncFromCloudinary = async () => {
    try {
      setIsSyncingCloudinary(true);
      const result = await syncGalleriesWithCloudinary();
      if (!result.success) {
        alert(`Cloudinary sync failed: ${result.message}`);
        return;
      }

      const refreshed = await loadLiveGalleries();
      onGalleriesChange(refreshed);
      window.dispatchEvent(new CustomEvent('galleries_updated'));

      alert(
        `${result.message}\n\nBefore: ${result.beforeCount ?? 'n/a'}\nAfter: ${result.afterCount ?? 'n/a'}\nRemoved galleries: ${result.removedGalleries ?? 0}\nRemoved images: ${result.removedImages ?? 0}`
      );
    } catch (error) {
      alert(`Cloudinary sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSyncingCloudinary(false);
    }
  };


  const workingGalleries = dragOrder ?? galleries;

  const activeCategory = selectedCategory
    ? categories.find(cat => cat.slug === selectedCategory)
    : null;
  const filteredGalleries = activeCategory
    ? workingGalleries.filter(gallery => sitePaths.galleryAssignedToCategory(gallery, activeCategory))
    : workingGalleries;

  const visibleGalleries = searchQuery.trim()
    ? filteredGalleries.filter((gallery) =>
        gallery.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
    : filteredGalleries;

  useEffect(() => {
    if (!openMenuId) return;
    const close = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('.gallery-menu-button')) return;
      if (!menuRef.current?.contains(target as Node)) {
        setOpenMenuId(null);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenMenuId(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenuId]);

  const moveGallery = (sourceId: string, targetId: string, list: Gallery[]): Gallery[] | null => {
    if (sourceId === targetId) return null;
    const updated = [...list];
    const from = updated.findIndex((gallery) => gallery.id === sourceId);
    const to = updated.findIndex((gallery) => gallery.id === targetId);
    if (from < 0 || to < 0 || from === to) return null;
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    return updated.map((gallery, index) => ({ ...gallery, order: index + 1 }));
  };

  const scrollWhileDragging = (clientY: number) => {
    const edge = 72;
    const scroller = document.querySelector('.admin-main') as HTMLElement | null;
    const canScroll = scroller && scroller.scrollHeight > scroller.clientHeight + 4;
    if (clientY < edge) {
      if (canScroll && scroller) scroller.scrollTop -= 16;
      else window.scrollBy(0, -16);
    } else if (clientY > window.innerHeight - edge) {
      if (canScroll && scroller) scroller.scrollTop += 16;
      else window.scrollBy(0, 16);
    }
  };

  const startReorder = (event: React.PointerEvent<HTMLButtonElement>, galleryId: string) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragSession.current = { pointerId: event.pointerId, sourceId: galleryId, origin: workingGalleries };
    latestOrder.current = null;
    setDraggingId(galleryId);
    setOpenMenuId(null);
  };

  const moveReorder = (event: React.PointerEvent<HTMLButtonElement>) => {
    const session = dragSession.current;
    if (!session || session.pointerId !== event.pointerId) return;
    event.preventDefault();
    scrollWhileDragging(event.clientY);
    const hit = document.elementFromPoint(event.clientX, event.clientY);
    const card = hit?.closest('[data-gallery-id]') as HTMLElement | null;
    const targetId = card?.dataset.galleryId;
    if (!targetId || targetId === session.sourceId) return;
    const next = moveGallery(session.sourceId, targetId, latestOrder.current ?? session.origin);
    if (!next) return;
    latestOrder.current = next;
    setDragOrder(next);
  };

  const finishReorder = (event: React.PointerEvent<HTMLButtonElement>) => {
    const session = dragSession.current;
    if (!session || session.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragSession.current = null;
    const next = latestOrder.current;
    const changed = !!next && next.some((gallery, index) => gallery.id !== session.origin[index]?.id);
    latestOrder.current = null;
    setDraggingId(null);
    setDragOrder(null);
    if (changed && next) {
      saveGalleries(next.map((gallery) => ({ ...gallery, updatedAt: new Date().toISOString() })));
    }
  };

  const nudgeGallery = (galleryId: string, direction: -1 | 1) => {
    const index = visibleGalleries.findIndex((gallery) => gallery.id === galleryId);
    const target = visibleGalleries[index + direction];
    if (!target) return;
    const next = moveGallery(galleryId, target.id, workingGalleries);
    if (!next) return;
    saveGalleries(next.map((gallery) => ({ ...gallery, updatedAt: new Date().toISOString() })));
  };

  return (
    <section className="admin-section gallery-admin">
        <div className="gallery-toolbar">
          <div className="gallery-toolbar-title">
            <h2>Galleries</h2>
            <span className="gallery-total">{galleries.length} {galleries.length === 1 ? 'gallery' : 'galleries'}</span>
          </div>
          <div className="gallery-toolbar-actions">
            <label className="gallery-search">
              <span className="sr-only">Search galleries</span>
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search galleries"
              />
            </label>
            <div className="view-toggle" role="group" aria-label="Gallery view">
              <button 
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid view"
                aria-pressed={viewMode === 'grid'}
              >
                <i className="fas fa-th" aria-hidden="true"></i>
              </button>
              <button 
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List view"
                aria-pressed={viewMode === 'list'}
              >
                <i className="fas fa-list" aria-hidden="true"></i>
              </button>
            </div>
            <button
              type="button"
              className="btn btn-quiet gallery-sync"
              onClick={handleSyncFromCloudinary}
              disabled={isSyncingCloudinary}
              aria-label="Sync from Cloudinary"
            >
              {isSyncingCloudinary ? 'Syncing…' : 'Sync'}
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => setShowCreateModal(true)}
            >
              <i className="fas fa-plus" aria-hidden="true"></i> Add Gallery
            </button>
          </div>
        </div>

        <div className="filter-section">
          <div className="filter-controls">
            <div className="filter-pills" role="group" aria-label="Filter by category">
              <button 
                className={`filter-pill ${selectedCategory === '' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('')}
              >
                All
                <span className="gallery-count">{galleries.length}</span>
              </button>
              {categoryOptionsWithCounts.map((option: { value: string; label: string; color: string; count: number }) => (
                <button 
                  key={option.value}
                  className={`filter-pill ${selectedCategory === option.value ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(option.value)}
                  style={{ '--pill-color': option.color } as React.CSSProperties}
                >
                  {option.label}
                  <span className="gallery-count">{option.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      
      <div 
        className={`galleries-container ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}
      >
        {visibleGalleries.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-images"></i>
            <h3>{galleries.length === 0 ? 'No galleries yet' : 'No matching galleries'}</h3>
            <p>{galleries.length === 0 ? 'Create your first gallery to get started' : 'Try a different search or category.'}</p>
            {galleries.length === 0 && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <i className="fas fa-plus"></i> Create Gallery
            </button>
            )}
          </div>
        ) : (
          visibleGalleries.map((gallery, index) => {
            const photoCount = gallery.images.length;
            const categoryLabels = sitePaths.galleryCategorySlugs(gallery).map((slug: string) => categoryLabel(slug));
            const notes = coverNotes(gallery);
            const position = gallery.order || index + 1;
            return (
            <div
              key={gallery.id}
              className={`gallery-item${draggingId === gallery.id ? ' is-dragging' : ''}`}
              data-gallery-id={gallery.id}
            >
              <div className="gallery-media">
                <a
                  className="gallery-preview"
                  href={galleryPreviewHref(gallery)}
                  target="_blank"
                  rel="noreferrer"
                  draggable={false}
                  aria-label={`Preview ${gallery.name}`}
                  onMouseDown={stopCardDrag}
                  onClick={stopCardDrag}
                >
                  <img
                    src={gallery.images && gallery.images[0] ?
                      applyHeroImagePositioning(
                        gallery.images[0].url || gallery.images[0].secure_url || '',
                        gallery.heroImagePosition || 'center',
                        400,
                        300,
                        gallery.heroImageZoom || 1.0,
                        gallery.heroImageCustomX,
                        gallery.heroImageCustomY,
                        gallery.heroImageCropBox
                      ) : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200"%3E%3Crect width="300" height="200" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E'
                    }
                    alt=""
                    style={{
                      objectPosition: getCSSObjectPosition(gallery.heroImagePosition || 'center')
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200"%3E%3Crect width="300" height="200" fill="%23f5f5f5"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%23999"%3EImage Error%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </a>
                <button
                  type="button"
                  className="gallery-reorder"
                  aria-label={`Reorder ${gallery.name}. Position ${position}. Drag, or use the arrow keys.`}
                  title="Drag to reorder"
                  onPointerDown={(event) => startReorder(event, gallery.id)}
                  onPointerMove={moveReorder}
                  onPointerUp={finishReorder}
                  onPointerCancel={finishReorder}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                      event.preventDefault();
                      nudgeGallery(gallery.id, -1);
                    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                      event.preventDefault();
                      nudgeGallery(gallery.id, 1);
                    }
                  }}
                >
                  <i className="fas fa-grip-vertical" aria-hidden="true"></i>
                  <span>{position}</span>
                </button>
              </div>
              <div className="gallery-content">
                <h3>{gallery.name}</h3>
                <p className="gallery-meta">
                  <span>{photoCount} {photoCount === 1 ? 'photo' : 'photos'}</span>
                  {categoryLabels.length > 0 && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span
                        className={categoryLabels.length > 1 ? 'gallery-meta-more' : undefined}
                        title={categoryLabels.length > 1 ? categoryLabels.join(', ') : undefined}
                      >
                        {categoryLabels.length > 1 ? `${categoryLabels.length} categories` : categoryLabels[0]}
                      </span>
                    </>
                  )}
                </p>
              </div>
              <div className="gallery-card-actions">
                <button
                  type="button"
                  className="btn btn-quiet gallery-edit"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpenMenuId(null);
                    setGalleryToEdit(gallery);
                    setShowEditModal(true);
                  }}
                >
                  <i className="fas fa-pen" aria-hidden="true"></i>
                  Edit
                </button>
                <div className="gallery-card-menu">
                  <button
                    type="button"
                    className="btn btn-quiet gallery-menu-button"
                    aria-label={`More actions for ${gallery.name}`}
                    aria-haspopup="menu"
                    aria-expanded={openMenuId === gallery.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (openMenuId === gallery.id) {
                        setOpenMenuId(null);
                        return;
                      }
                      const rect = event.currentTarget.getBoundingClientRect();
                      const width = 200;
                      const estimated = 140;
                      let top = rect.bottom + 6;
                      if (top + estimated > window.innerHeight - 8) top = Math.max(8, rect.top - estimated - 6);
                      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
                      setMenuBox({ top, left });
                      setOpenMenuId(gallery.id);
                    }}
                  >
                    <i className="fas fa-ellipsis-h" aria-hidden="true"></i>
                  </button>
                  {openMenuId === gallery.id && menuBox ? createPortal(
                    <div
                      ref={menuRef}
                      className="gallery-card-menu-panel"
                      role="menu"
                      style={{ position: 'fixed', top: menuBox.top, left: menuBox.left }}
                    >
                      <a
                        role="menuitem"
                        href={galleryPreviewHref(gallery)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => setOpenMenuId(null)}
                      >
                        Preview
                      </a>
                      {notes.map((note) => (
                        <p key={note}>{note}</p>
                      ))}
                    </div>,
                    document.body,
                  ) : null}
                </div>
              </div>
            </div>
            );
          })
        )}
      </div>

      {/* Modern Gallery Creation Modal */}
      <GalleryCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateGallery}
      />


      {/* Gallery Edit Modal */}
      <GalleryEditModal
        gallery={galleryToEdit}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setGalleryToEdit(null);
        }}
        onSave={handleEditGallery}
        onDelete={galleryToEdit ? () => {
          if (galleryToEdit) {
            deleteGallery(galleryToEdit.id);
            setGalleryToEdit(null);
            setShowEditModal(false);
          }
        } : undefined}
      />

    </section>
  );
};

export default GalleryManagement;
